import { IconSystemV2CourseArrowChevronDownArrowDown } from '@doltech/icons/SystemV2/IconSystemV2CourseArrowChevronDownArrowDown';
import { IconSystemV2ContentUnderlineWord } from '@doltech/icons/SystemV2/IconSystemV2ContentUnderlineWord';
import { ExpandCollapseAnimation } from '@doltech/ui/lib/examples/Animation/ExpandCollapse';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { shadows } from '@doltech/ui/lib/figma/shadows';
import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { flexGap } from '@doltech/utils/lib/css-style';

const Main = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${shadows.zIndex1};
`;

const Header = styled.div`
  background-color: ${colorsV2.white100};
  padding: 16px 20px;
  border-radius: 12px 12px 0px 0px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  &.expanded {
    box-shadow: ${shadows.border1Bottom};
    svg {
      transform: rotate(180deg);
      transition: transform 0.2s;
    }
  }
`;

const Content = styled.div`
  display: grid;
  gap: 10px;
  background-color: ${colorsV2.white100};
  padding: 20px;
  .word-italic {
    font-style: italic;
  }
  .en-sub {
    text-decoration: underline dotted ${colorsV2.grey40} 2px;
    text-underline-offset: 6px;
    cursor: pointer;
  }
`;

const EnHeader = styled.div`
  display: flex;
  align-items: center;
  ${flexGap(8)};
`;

const EnContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${flexGap([8, 8])};
`;

const VietSub = styled.div`
  border-top: 1px solid ${colorsV2.grey10};
`;

interface SectionViewScriptDetailProps {
  title: string;
  words: {
    key?: number;
    type?: string;
    value?: string;
  }[];
  viSub: string;
  shadowing?: React.ReactNode;
  handleReadTerm?: (term: string) => void;
}

export const SectionViewScriptDetail = (props: SectionViewScriptDetailProps) => {
  const { title, words, viSub, shadowing, handleReadTerm } = props;
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Main className="section-view-script-detail-layout">
      <Header onClick={() => setExpanded((o) => !o)} className={cl({ expanded })}>
        <ResponsiveTypography.Paragraph variant="semi-bold/16-24">
          {title}
        </ResponsiveTypography.Paragraph>
        <IconSystemV2CourseArrowChevronDownArrowDown size={24} color="black60" />
      </Header>

      <ExpandCollapseAnimation expanded={expanded}>
        <Content>
          <EnHeader>
            <ResponsiveTypography.Paragraph variant="medium/14-20" color="grey120" uppercase>
              English
            </ResponsiveTypography.Paragraph>
            <IconSystemV2ContentUnderlineWord size={16} color="grey140" />
            <ResponsiveTypography.Paragraph variant="regular/14-20" color="grey120">
              Click vào từ để nghe phát âm
            </ResponsiveTypography.Paragraph>
          </EnHeader>
          <EnContent>
            {words?.map((eachWord, idx) => (
              <ResponsiveTypography.Paragraph
                variant="regular/14-20"
                color="grey180"
                className={cl({ 'en-sub': eachWord?.type !== 'PUNCTUATION' })}
                key={idx}
                onClick={
                  eachWord?.type !== 'PUNCTUATION' ? () => handleReadTerm?.(eachWord?.value) : null
                }
              >
                {eachWord?.value}
              </ResponsiveTypography.Paragraph>
            ))}
          </EnContent>
          {shadowing}
        </Content>
        {viSub && (
          <VietSub>
            <Content>
              <ResponsiveTypography.Paragraph variant="medium/14-20" color="grey120" uppercase>
                Vietnamese
              </ResponsiveTypography.Paragraph>
              <ResponsiveTypography.Paragraph
                variant="regular/14-20"
                color="grey180"
                className="word-italic"
              >
                {viSub}
              </ResponsiveTypography.Paragraph>
            </Content>
          </VietSub>
        )}
      </ExpandCollapseAnimation>
    </Main>
  );
};
